import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/layout/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
    ShieldCheck, PauseCircle, PlayCircle,
    RefreshCw, Copy, ExternalLink, AlertTriangle,
    ArrowRightLeft, Upload, CheckCircle,
    Key, Package, Cpu, ChevronDown, ChevronUp,
    Wallet, Zap
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const POLYGONSCAN = 'https://polygonscan.com';
const PROXY_ADDRESS = '0x646d7dbBd093049fC5f567c8A97A41f01F058355';

// Minimal ABI — only what we need
const CONTRACT_ABI = [
    "function owner() view returns (address)",
    "function pendingOwner() view returns (address)",
    "function paused() view returns (bool)",
    "function version() view returns (string)",
    "function transferOwnership(address newOwner)",
    "function acceptOwnership()",
    "function pause()",
    "function unpause()",
];

// ── Small helpers ──────────────────────────────────────────────
const shortAddr = (a) => a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '—';

const AddressRow = ({ label, value, note }) => {
    const { t } = useLanguage();
    const copy = () => {
        if (!value) return;
        navigator.clipboard.writeText(value);
        toast.success(t('common.copied', 'Copiado al portapapeles'));
    };
    return (
        <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
            <div className="flex-1 min-w-0">
                <p className="text-xs text-brand-text font-medium uppercase tracking-wide">{label}</p>
                {note && <p className="text-[10px] text-brand-text mt-0.5">{note}</p>}
                <p className="font-mono text-sm text-brand-dark mt-0.5 break-all">{value || '—'}</p>
            </div>
            <div className="flex gap-1.5 flex-shrink-0 ml-3">
                {value && (
                    <>
                        <button onClick={copy} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" title={t('common.copy', 'Copiar')}>
                            <Copy className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        <a href={`${POLYGONSCAN}/address/${value}`} target="_blank" rel="noreferrer"
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" title={t('common.view_on_scan', 'Ver en PolygonScan')}>
                            <ExternalLink className="w-3.5 h-3.5 text-gray-500" />
                        </a>
                    </>
                )}
            </div>
        </div>
    );
};

const TxLink = ({ hash }) => {
    if (!hash) return null;
    return (
        <a href={`${POLYGONSCAN}/tx/${hash}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-brand hover:underline font-mono text-xs">
            {hash.slice(0, 10)}...{hash.slice(-8)}
            <ExternalLink className="w-3 h-3" />
        </a>
    );
};

// ── MetaMask section — connect wallet and call acceptOwnership ──
const MetaMaskSection = ({ pendingOwner, onAccepted }) => {
    const { t } = useLanguage();
    const [connected, setConnected] = useState(false);
    const [account, setAccount] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [lastTxHash, setLastTxHash] = useState(null);

    const hasPending = pendingOwner &&
        pendingOwner !== '0x0000000000000000000000000000000000000000';

    const isPendingOwner = hasPending && account &&
        account.toLowerCase() === pendingOwner.toLowerCase();

    const connectMetaMask = async () => {
        if (!window.ethereum) {
            toast.error(t('contract_manager.metamask_not_installed', 'MetaMask no está instalado. Instalalo desde metamask.io'));
            return;
        }
        try {
            const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
            setAccount(accounts[0]);
            setConnected(true);

            // Switch to Polygon if needed
            const chainId = await window.ethereum.request({ method: 'eth_chainId' });
            if (chainId !== '0x89') { // 0x89 = 137 decimal = Polygon
                try {
                    await window.ethereum.request({
                        method: 'wallet_switchEthereumChain',
                        params: [{ chainId: '0x89' }],
                    });
                } catch {
                    toast.error(t('contract_manager.switch_polygon_error', 'Cambiá la red a Polygon Mainnet en MetaMask'));
                }
            }
        } catch {
            toast.error(t('contract_manager.connect_error', 'Error al conectar MetaMask'));
        }
    };

    // Listen for account changes
    useEffect(() => {
        if (!window.ethereum) return;
        const handleChange = (accounts) => {
            setAccount(accounts[0] || null);
            setConnected(!!accounts[0]);
        };
        window.ethereum.on('accountsChanged', handleChange);
        return () => window.ethereum.removeListener('accountsChanged', handleChange);
    }, []);

    const acceptOwnership = async () => {
        if (!window.ethereum || !account) return;
        if (!window.confirm(`${t('common.confirm', '¿Confirmas')} ${t('contract_manager.accept_ownership_confirm', 'aceptar el ownership del contrato')}?\n\n${t('common.your_wallet', 'Tu wallet')}: ${account}\n${t('common.contract', 'Contrato')}: ${PROXY_ADDRESS}`)) return;

        setProcessing(true);
        try {
            // Build calldata for acceptOwnership() — selector is 0x79ba5097
            const data = '0x79ba5097';

            const txHash = await window.ethereum.request({
                method: 'eth_sendTransaction',
                params: [{
                    from: account,
                    to: PROXY_ADDRESS,
                    data,
                    chainId: '0x89',
                }],
            });

            setLastTxHash(txHash);
            toast.success(t('contract_manager.tx_sent', 'Transacción enviada. Esperando confirmación...'));

            // Poll until mined (max 90 seconds)
            let mined = false;
            for (let i = 0; i < 18; i++) {
                await new Promise(r => setTimeout(r, 5000));
                const receipt = await window.ethereum.request({
                    method: 'eth_getTransactionReceipt',
                    params: [txHash],
                });
                if (receipt && receipt.status === '0x1') {
                    mined = true;
                    break;
                } else if (receipt && receipt.status === '0x0') {
                    toast.error(t('contract_manager.tx_reverted', 'TX revertida — puede que ya hayas aceptado o que no seas el pendingOwner'));
                    setProcessing(false);
                    return;
                }
            }

            if (mined) {
                toast.success(t('contract_manager.ownership_accepted', '🎉 ¡Ownership aceptado! Sos el nuevo owner del contrato.'));
                onAccepted && onAccepted(txHash);
            } else {
                toast(t('contract_manager.tx_sent_not_confirmed', 'TX enviada pero no confirmada todavía. Revisá PolygonScan.'), { icon: '⏳' });
            }
        } catch (err) {
            toast.error(t('common.error', 'Error') + ': ' + (err.message || t('common.tx_rejected', 'TX rechazada')));
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-brand-dark mb-4 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-orange-500" />
                {t('contract_manager.accept_title', 'Aceptar Ownership')}
                {hasPending && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                        {t('contract_manager.status_pending', 'Pendiente')}
                    </span>
                )}
            </h2>

            {!hasPending ? (
                <p className="text-sm text-brand-text">{t('contract_manager.no_pending_owner', 'No hay transferencia de ownership pendiente.')}</p>
            ) : (
                <div className="space-y-4">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                        <p className="font-semibold mb-1">⚠️ {t('contract_manager.pending_warning_title', 'Transferencia pendiente de aceptación')}</p>
                        <p>{t('contract_manager.pending_proposal_to', 'El ownership fue propuesto a:')}</p>
                        <p className="font-mono mt-1 break-all">{pendingOwner}</p>
                        <p className="mt-2">{t('contract_manager.pending_warning_note', 'Esa wallet debe conectarse y aceptar explícitamente.')}</p>
                    </div>

                    {!connected ? (
                        <button
                            onClick={connectMetaMask}
                            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition">
                            <Wallet className="w-5 h-5" />
                            {t('contract_manager.connect_metamask', 'Conectar MetaMask')}
                        </button>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm">
                                <div className="w-2 h-2 rounded-full bg-green-500" />
                                <span className="text-brand-text">{t('contract_manager.connected', 'Conectado')}:</span>
                                <span className="font-mono text-brand-dark">{shortAddr(account)}</span>
                                {isPendingOwner && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                                        ✓ {t('contract_manager.is_pending_owner', 'Es el pending owner')}
                                    </span>
                                )}
                            </div>

                            {!isPendingOwner && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                                    ⚠️ {t('contract_manager.not_pending_owner_warning', 'La wallet conectada no es el propietario pendiente. Conecta la wallet')} <span className="font-mono">{shortAddr(pendingOwner)}</span>.
                                </div>
                            )}

                            {isPendingOwner && (
                                <button
                                    onClick={acceptOwnership}
                                    disabled={processing}
                                    className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition disabled:opacity-50">
                                    {processing
                                        ? <RefreshCw className="w-5 h-5 animate-spin" />
                                        : <Zap className="w-5 h-5" />}
                                    {processing ? t('common.processing', 'Procesando...') : t('contract_manager.accept_title', 'Aceptar Ownership')}
                                </button>
                            )}

                            {lastTxHash && (
                                <div className="flex items-center gap-2 text-xs text-brand-text">
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    TX: <TxLink hash={lastTxHash} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


// ── Main Component ─────────────────────────────────────────────
export const ContractManagerPage = () => {
    const { token } = useAuth();
    const { t, currentLanguage } = useLanguage();

    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processingPause, setProcessingPause] = useState(false);
    const [processingTransfer, setProcessingTransfer] = useState(false);
    const [newOwnerInput, setNewOwnerInput] = useState('');
    const [lastTx, setLastTx] = useState(null);
    const [showUpgradeSection, setShowUpgradeSection] = useState(false);

    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchInfo = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/admin/blockchain/contract-info', { headers });
            if (!res.ok) throw new Error(await res.text());
            setInfo(await res.json());
        } catch (err) {
            toast.error(t('contract_manager.fetch_error', 'Error cargando datos del contrato'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [token, t]);

    useEffect(() => { fetchInfo(); }, [fetchInfo]);

    // ── Pause / Unpause ────────────────────────────────────────
    const handlePause = async () => {
        const shouldPause = !info?.isPaused;
        const action = shouldPause ? t('common.pause', 'PAUSAR') : t('common.resume', 'REANUDAR');
        if (!window.confirm(`${t('common.confirm', '¿Confirmas')} ${action} ${t('common.the_contract', 'el contrato')}?`)) return;
        setProcessingPause(true);
        try {
            const res = await fetch('/api/admin/blockchain/pause', {
                method: 'POST', headers,
                body: JSON.stringify({ pause: shouldPause })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            setInfo(prev => ({ ...prev, isPaused: shouldPause }));
            setLastTx({ action, hash: data.txHash });
            toast.success(`${t('common.contract', 'Contrato')} ${shouldPause ? t('common.paused_label', 'pausado') : t('common.active_label', 'reanudado')} — TX: ${data.txHash?.slice(0, 10)}...`);
        } catch (err) {
            toast.error(t('common.error', 'Error') + ': ' + err.message);
        } finally {
            setProcessingPause(false);
        }
    };

    // ── Transfer Ownership (propose) ──────────────────────────
    const handleTransferOwnership = async () => {
        const addr = newOwnerInput.trim();
        if (!/^0x[0-9a-fA-F]{40}$/.test(addr)) {
            toast.error(t('contract_manager.invalid_address', 'Address inválido — debe ser 0x seguido de 40 caracteres hex'));
            return;
        }
        if (!window.confirm(`${t('common.confirm', '¿Confirmas')} ${t('contract_manager.propose_to', 'proponer el ownership a')} ${addr}?\n\n${t('contract_manager.propose_note_confirm', 'Esa wallet deberá aceptar explícitamente.')}`)) return;

        setProcessingTransfer(true);
        try {
            const res = await fetch('/api/admin/blockchain/transfer-ownership', {
                method: 'POST', headers,
                body: JSON.stringify({ newOwner: addr })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            setLastTx({ action: 'TRANSFER_OWNERSHIP', hash: data.txHash });
            setInfo(prev => ({ ...prev, pendingOwner: addr }));
            setNewOwnerInput('');
            toast.success(t('common.success_title', '✅ Propuesta enviada'));
        } catch (err) {
            toast.error(t('common.error', 'Error') + ': ' + err.message);
        } finally {
            setProcessingTransfer(false);
        }
    };

    if (loading && !info) return (
        <Layout>
            <div className="flex items-center justify-center h-screen">
                <RefreshCw className="w-8 h-8 animate-spin text-brand" />
            </div>
        </Layout>
    );

    const isPaused = info?.isPaused;
    const hasPendingOwner = info?.pendingOwner &&
        info.pendingOwner !== '0x0000000000000000000000000000000000000000';

    return (
        <Layout>
            <div className="p-6 space-y-6 max-w-5xl mx-auto">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-brand-dark flex items-center gap-2">
                            <Cpu className="w-7 h-7 text-brand" />
                            {t('contract_manager.title', 'Contract Manager')}
                        </h1>
                        <p className="text-sm text-brand-text mt-1">{t('contract_manager.subtitle', 'Gestión del contrato UUPS en Polygon Mainnet')}</p>
                    </div>
                    <button onClick={fetchInfo} disabled={loading}
                        className="p-2 bg-white border border-gray-200 rounded-full hover:bg-gray-50 shadow-sm transition">
                        <RefreshCw className={`w-5 h-5 text-brand-text ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Status Banner */}
                <div className={`flex items-center gap-3 px-5 py-4 rounded-xl border-2 ${isPaused
                    ? 'bg-red-50 border-red-200'
                    : 'bg-emerald-50 border-emerald-200'}`}>
                    {isPaused
                        ? <PauseCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                        : <PlayCircle className="w-6 h-6 text-emerald-500 flex-shrink-0" />}
                    <div className="flex-1">
                        <p className={`font-bold text-sm ${isPaused ? 'text-red-700' : 'text-emerald-700'}`}>
                            {t('common.contract', 'Contrato')} {isPaused ? t('common.paused', 'PAUSADO') : t('common.active', 'ACTIVO')}
                        </p>
                        <p className={`text-xs mt-0.5 ${isPaused ? 'text-red-500' : 'text-emerald-500'}`}>
                            {isPaused
                                ? t('contract_manager.pause_description', 'registerDocument y revokeDocument están bloqueados.')
                                : t('contract_manager.active_description', 'Todas las funciones están operativas.')}
                        </p>
                    </div>
                    <button onClick={handlePause} disabled={processingPause}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${processingPause
                            ? 'bg-gray-200 text-brand-text cursor-not-allowed'
                            : isPaused
                                ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                : 'bg-red-500 hover:bg-red-600 text-white'}`}>
                        {processingPause && <RefreshCw className="w-4 h-4 animate-spin" />}
                        {isPaused ? `▶ ${t('common.resume', 'Reanudar')}` : `⏸ ${t('common.pause', 'Pausar')}`}
                    </button>
                </div>

                {/* Main Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* Contract Addresses */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-base font-semibold text-brand-dark mb-4 flex items-center gap-2">
                            <Package className="w-5 h-5 text-brand" />
                            {t('contract_manager.addresses_title', 'Addresses del Contrato')}
                        </h2>
                        <div className="space-y-1">
                            <AddressRow
                                label={t('contract_manager.proxy_label', 'Proxy (UUPS)')}
                                value={info?.proxyAddress}
                                note={t('contract_manager.proxy_note', 'Esta dirección nunca cambia en upgrades futuros')}
                            />
                            <AddressRow
                                label={t('contract_manager.implementation_label', 'Implementación actual')}
                                value={info?.implementationAddress}
                                note={t('contract_manager.implementation_note', 'Cambia en cada upgrade')}
                            />
                            <AddressRow
                                label={t('contract_manager.relayer_label', 'Relayer (deployer)')}
                                value={info?.relayerAddress}
                                note={t('contract_manager.relayer_note', 'Wallet que firma las txs del relayer')}
                            />
                        </div>
                        <div className="mt-4 flex items-center gap-2 text-xs text-brand-text">
                            <span className="font-medium">{t('contract_manager.version', 'Versión')}:</span>
                            <span className="bg-blue-50 text-brand px-2 py-0.5 rounded font-mono">{info?.version || '—'}</span>
                        </div>
                    </div>

                    {/* Ownership info + propose */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-base font-semibold text-brand-dark mb-4 flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-brand" />
                            {t('contract_manager.ownership_title', 'Ownership')}
                        </h2>
                        <div className="space-y-1">
                            <AddressRow label={t('contract_manager.owner_label', 'Owner actual')} value={info?.owner} />
                            <AddressRow
                                label={t('contract_manager.pending_owner_label', 'Pending owner')}
                                value={hasPendingOwner ? info?.pendingOwner : null}
                                note={hasPendingOwner ? t('contract_manager.pending_owner_note', 'Debe aceptar el ownership') : undefined}
                            />
                        </div>
                        {!hasPendingOwner && (
                            <p className="text-xs text-brand-text mt-3">{t('contract_manager.no_transfer_pending', 'Sin transferencia pendiente')}</p>
                        )}

                        {/* Transfer Form */}
                        <div className="mt-5 pt-4 border-t border-gray-100">
                            <p className="text-xs font-semibold text-brand-text mb-2 flex items-center gap-1.5">
                                <ArrowRightLeft className="w-3.5 h-3.5" />
                                {t('contract_manager.propose_owner_label', 'Proponer nuevo owner')}
                            </p>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder={t('common.address_placeholder', '0x...')}
                                    value={newOwnerInput}
                                    onChange={e => setNewOwnerInput(e.target.value)}
                                    className="flex-1 text-sm border rounded-lg px-3 py-2 font-mono focus:ring-2 focus:ring-brand outline-none"
                                />
                                <button onClick={handleTransferOwnership}
                                    disabled={processingTransfer || !newOwnerInput}
                                    className="px-4 py-2 bg-brand hover:bg-brand-dark text-white text-sm rounded-lg font-medium transition disabled:opacity-50 flex items-center gap-1.5 flex-shrink-0">
                                    {processingTransfer
                                        ? <RefreshCw className="w-4 h-4 animate-spin" />
                                        : <Key className="w-4 h-4" />}
                                    {t('contract_manager.propose_btn', 'Proponer')}
                                </button>
                            </div>
                            <p className="text-[10px] text-brand-text mt-1.5">
                                ⚠️ {t('contract_manager.propose_note', 'El nuevo owner debe aceptar con MetaMask (sección de abajo).')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Accept Ownership — MetaMask */}
                <MetaMaskSection
                    pendingOwner={info?.pendingOwner}
                    onAccepted={(hash) => {
                        setLastTx({ action: 'ACCEPT_OWNERSHIP', hash });
                        fetchInfo();
                    }}
                />

                {/* Last Transaction */}
                {lastTx && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-xs font-semibold text-brand-text">{t('common.last_action', 'Última acción')}: {lastTx.action}</p>
                            <div className="flex items-center gap-1">
                                <span className="text-[10px] text-gray-400 font-bold">{t('common.tx_label', 'TX')}:</span>
                                <TxLink hash={lastTx.hash} />
                            </div>
                        </div>
                        <button onClick={() => setLastTx(null)} className="text-gray-300 hover:text-brand-text text-xs">✕</button>
                    </div>
                )}

                {/* Upgrade Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <button
                        onClick={() => setShowUpgradeSection(v => !v)}
                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition text-left">
                        <div className="flex items-center gap-2">
                            <Upload className="w-5 h-5 text-purple-500" />
                            <span className="font-semibold text-brand-dark">{t('contract_manager.upgrade_title', 'Upgrade del Contrato')}</span>
                            <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full font-medium">UUPS</span>
                        </div>
                        {showUpgradeSection
                            ? <ChevronUp className="w-4 h-4 text-brand-text" />
                            : <ChevronDown className="w-4 h-4 text-brand-text" />}
                    </button>

                    {showUpgradeSection && (
                        <div className="px-6 pb-6 space-y-4 border-t border-gray-100">
                            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-sm font-semibold text-amber-700 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    {t('contract_manager.upgrade_terminal_note', 'El upgrade se hace desde la línea de comandos')}
                                </p>
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-xs font-medium text-brand-text mb-1">1. {t('common.compile', 'Compilar')}</p>
                                    <code className="block bg-gray-900 text-green-400 text-xs px-4 py-3 rounded-lg font-mono">
                                        cd backend && npx hardhat compile
                                    </code>
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-brand-text mb-1">2. {t('common.deploy_upgrade', 'Deploy upgrade')}</p>
                                    <code className="block bg-gray-900 text-green-400 text-xs px-4 py-3 rounded-lg font-mono">
                                        npx hardhat run scripts/upgrade.cjs --network polygon
                                    </code>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <a href={`${POLYGONSCAN}/address/${info?.proxyAddress}#writeProxyContract`}
                                    target="_blank" rel="noreferrer"
                                    className="flex items-center gap-1.5 text-xs text-brand hover:underline">
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    {t('contract_manager.view_write_proxy', 'Ver Write Proxy en PolygonScan')}
                                </a>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </Layout>
    );
};
